/* eslint-disable no-underscore-dangle */
// eslint-disable-next-line import/no-cycle
import GraphStore from '@src/stores/GraphStore';
import { register } from '@src/dispatcher';

// Constants
import {
  ActionTypes,
  EventTypes,
  DEFAULT_LANGUAGE,
  STORAGE_KEYS,
} from '@src/constants/MogamboConstants';

// Utils
import {
  storeUserPreference,
  getUserPreference,
} from '@src/utils/UserPrefsUtils';

// Local
import createStore from './StoreUtils.js';

/**
 * Common Store
 * All the store values that are related to metas (current step, domain, etc)
 * and the step summary are stored here
 * @type {[type]}
 */
const CommonStore = createStore({
  /**
   * _currentDomain: The current bot selected in the app
   * _currentStory: The current story selected in the app
   * _currentSubStory: The current substory selected in the app
   * _currentStep: The current step selected in the app
   * @type {[type]}
   */
  _currentScreen: '',
  _currentDomain: {},
  _currentStory: {},
  _currentSubStory: {},
  _currentStep: {},
  _saveCallback: undefined,
  _originalResponses: undefined,
  _currentLanguage: {
    languageScript: DEFAULT_LANGUAGE,
    languageSource: DEFAULT_LANGUAGE,
  },
  _supportedLanguages: [DEFAULT_LANGUAGE],
  _allLanguages: [],
  _languagesCodeToTitle: {},
  // Toggles true when user edits something and tries to navigate away from page
  _editMode: false,
  _globalConstantsDrawerActive: false,
  _abTesting: {
    drawerActive: false,
    rhsState: '',
    stepSelectedForABTesting: {},
    abTestInfo: {
      data: [],
      exists: false,
    },

    isModified: false,
  },
  _genAiActive: false,
  /**
   * Set all the meta tags such as current story, domain etc.
   * We use destructuring to deep clone an object
   * @param {Object} [meta={}] [description]
   */
  setMetaTags(meta = {}, callback) {
    let domainChanged = false;
    if (this._currentDomain.id !== meta.domain.id) {
      domainChanged = true;
    }
    this._currentDomain = { ...meta.domain };
    this._currentStory = { ...meta.story };
    this._currentSubStory = { ...meta.subStory };
    this._currentStep = { ...meta.step };
    this.setSupportedLanguages([...meta.domain.language_list]);
    this._allLanguages = [...meta.all_languages];
    this._currentConnection = { ...meta.connection };

    // Hybrid language operations
    this._allHybridLanguages = { ...meta.all_hybrid_languages };
    this._hybridLanguageSources = {};
    this._hybridLanguageList = Object.keys(this._allHybridLanguages);

    if (this._hybridLanguageList?.length > 0) {
      this._hybridLanguageList.forEach((item) => {
        Object.keys(this._allHybridLanguages[item]).forEach((innerItem) => {
          const { script } = this._allHybridLanguages[item][innerItem];
          const hybridLanguageKey = `${script}|${item}`;
          if (
            item !== script &&
            !this._supportedLanguages.includes(hybridLanguageKey)
          ) {
            this._hybridLanguageSources[hybridLanguageKey] = item;
            this._supportedLanguages.push(hybridLanguageKey);
          }
        });
      });
    }

    /**
     * _languageCodeToTitle will store the title for each language and script code.
     * For the languages, it will store like this - en: 'English'
     * And for the hybrid languages, it will store the value in script|language format like en|hi: Hinglish
     */
    this._allLanguages.map((language) => {
      this._languagesCodeToTitle = {
        ...this._languagesCodeToTitle,
        [language.code]: language?.name,
      };
    });
    Object.keys(this._allHybridLanguages).map((language) => {
      // To consider hybrid languages only when two scripts are present inside one language.
      if (this._allHybridLanguages[language].length > 1) {
        this._allHybridLanguages[language].map((index) => {
          this._languagesCodeToTitle = {
            ...this._languagesCodeToTitle,
            [`${index.script}|${language}`]: index.native_name,
          };
        });
      }
    });

    /**
     * If the user directly navigates to a bot using the link then
     * the topbar doesn't gets updated with the new bot details.
     * So to handle this case, setting the ww_iva details in local storage
     */
    /* TODO: Write this in one util function - 
       Similar logic is used in TemplatesAPI updateTemplate function also */
    const selectedIVA = JSON.parse(localStorage.getItem('ww_iva'));
    if (selectedIVA.id !== meta.domain.id) {
      if (selectedIVA.isSmartSkill) {
        selectedIVA.name = meta.domain.title;
        selectedIVA.domain_id = meta.domain.id;
      } else {
        selectedIVA.title = meta.domain.title;
        selectedIVA.id = meta.domain.id;
      }
      localStorage.setItem('ww_iva', JSON.stringify(selectedIVA));
    }

    this._saveCallback = undefined;
    if (callback) setTimeout(() => callback(meta), 0);

    // If the bot changes => check if isn't supported by this bot => revert to the language
    if (domainChanged) {
      const languageScript = getUserPreference(
        `${STORAGE_KEYS.BOT_LANGUAGE_SCRIPT}-${this._currentDomain.id}`,
        DEFAULT_LANGUAGE
      );
      const languageSrc = getUserPreference(
        `${STORAGE_KEYS.BOT_LANGUAGE_SOURCE}-${this._currentDomain.id}`,
        DEFAULT_LANGUAGE
      );
      this.setCurrentLanguage(languageScript, languageSrc);
    }
  },

  /**
   * Getter for getting all the meta tags
   * We use destructuring to deep clone an object so that componentWillReceiveProps
   * can work as intended
   * @return {[type]} [description]
   */
  getMetaTags() {
    // We've got to clone to allow react to know things have changed
    return {
      allLanguages: [...this._allLanguages],
      allHybridLanguages: this._allHybridLanguages,
      domain: { ...this._currentDomain },
      story: { ...this._currentStory },
      subStory: { ...this._currentSubStory },
      step: { ...this._currentStep },
      connection: { ...this._currentConnection },
      hybridLanguageSources: { ...this._hybridLanguageSources },
    };
  },

  /**
   * We set the step summary as a key inside the current step itself
   * as the summary is always for the current step
   * @param {[type]} summary [description]
   */
  setStepSummary(summary) {
    const summaryData = summary[0];
    if (summaryData && this._currentStep.id === summaryData.meta.id) {
      this._currentStep.summary = summary.length && summary[0];
      this._newStep = null;
    }
  },

  updateCurrentStepPosition({ id, x, y }) {
    if (
      this._currentStep &&
      this._currentStep.id === id &&
      this._currentStep.summary &&
      this._currentStep.summary.meta
    ) {
      this._currentStep.summary = {
        ...this._currentStep.summary,
        meta: {
          ...this._currentStep.summary.meta,
          x,
          y,
        },
      };
    } else if (id.startsWith('new')) {
      this._newStep = {
        x,
        y,
      };
    }
  },

  setEditMode(flag) {
    this._editMode = flag;
  },

  getEditMode() {
    return this._editMode;
  },

  getNewStepPosition() {
    return this._newStep;
  },

  /**
   * Get Step Summary
   * @return {[type]} [description]
   */
  getStepSummary() {
    return this._currentStep.summary;
  },

  /**
   * Set the different node connections for the current node
   * @param {[type]} connections [description]
   */
  setStepConnections(connections, isUpdate) {
    this._currentStep.connections = connections;
    if (!isUpdate) {
      this._originalResponses = JSON.parse(JSON.stringify(connections));
    }
  },

  /**
   * Getter to get all the step connections
   * When getting the connections, we also populate the color key with right
   * node colors by reading from the Graph Store
   * @return {[type]} [description]
   */
  getStepConnections() {
    return this._currentStep.connections
      ? this._currentStep.connections.map((connection) => {
          connection.color = GraphStore.getStepColor(connection.id);
          return connection;
        })
      : undefined;
  },

  /**
   * Set the save callback for when editing bot says/user says/settings
   * etc. It sets the callback to be executed later when the user hits
   * the save button
   * @param {Function} callback [description]
   */
  setSaveCallback(callback) {
    this._saveCallback = callback;
  },

  /**
   * Getter for the save callback. The function's existence denotes that
   * there are changes that are yet to be saved
   * @return {[type]} [description]
   */
  getSaveCallback() {
    return this._saveCallback;
  },

  /**
   * Reset all edited responses
   */
  resetEdited() {
    if (this._originalResponses) {
      this.setStepConnections(this._originalResponses, true);
    }
  },

  /**
   * @description Get the current language mode of the bot
   * @author jatin.parekh
   * @returns Current Language mode of the bot
   */
  getCurrentLanguage() {
    return this._currentLanguage;
  },

  /**
   * @description Update the current language mode of the mode
   * @author jatin.parekh
   * @param {string} languageSource
   * @param {string} languageScript
   */
  setCurrentLanguage(languageSource, languageScript) {
    this._currentLanguage = {
      languageSource,
      languageScript,
    };

    storeUserPreference(
      `${STORAGE_KEYS.BOT_LANGUAGE_SCRIPT}-${this._currentDomain.id}`,
      languageScript
    );
    storeUserPreference(
      `${STORAGE_KEYS.BOT_LANGUAGE_SOURCE}-${this._currentDomain.id}`,
      languageSource
    );
  },

  setCurrentScreen(screen) {
    this._currentScreen = screen;
  },

  getCurrentScreen() {
    return this._currentScreen;
  },

  setSupportedLanguages(supportedLanguages) {
    this._supportedLanguages = supportedLanguages;
  },

  getSupportedLanguages() {
    return this._supportedLanguages;
  },

  getAllLanguages() {
    return this._allLanguages;
  },

  getLanguagesCodeToTitle() {
    return this._languagesCodeToTitle;
  },

  getAllHybridLanguages() {
    return this._allHybridLanguages;
  },

  setGlobalConstantsDrawerFlag(flag) {
    this._globalConstantsDrawerActive = flag;
  },

  getGlobalConstantsDrawerFlag() {
    return this._globalConstantsDrawerActive;
  },

  setABTestingDrawerFlag(flag) {
    this._abTesting.drawerActive = flag;
  },

  getABTestingDrawerFlag() {
    return this._abTesting.drawerActive;
  },

  setABTestingRhsState(rhsState) {
    this._abTesting.rhsState = rhsState;
  },

  getABTestingRhsState() {
    return this._abTesting.rhsState;
  },

  setStepSelectedForABTesting(step) {
    this._abTesting.stepSelectedForABTesting = step;
  },

  getStepSelectedForABTesting() {
    return this._abTesting.stepSelectedForABTesting;
  },

  setABTestInfo(info) {
    this._abTesting.abTestInfo = info ?? {};
  },

  getABTestInfo() {
    return this._abTesting.abTestInfo;
  },

  setABTestModifiedFlag(flag) {
    this._abTesting.isModified = flag;
  },

  setGenAiModalActive(flag) {
    this._genAiActive = flag;
  },

  getGenAiModalActive() {
    return this._genAiActive;
  },

  getABTestModifiedFlag() {
    return this._abTesting.isModified;
  },
});

/**
 * Listen to actions that are dispatched by the dispatcher and accordingly
 * call some store functions depending on the ActionType
 * @type {[type]}
 */
CommonStore.dispatchToken = register((action) => {
  const actionType = action.type;
  action = action.action;

  switch (actionType) {
    case ActionTypes.SET_CURRENT_SCREEN:
      CommonStore.setCurrentScreen(action.screen);
      CommonStore.emitChange();
      break;
    case ActionTypes.SET_URL_METADATA:
      CommonStore.setMetaTags(action.metaTags, action.callback);
      CommonStore.emitChange();
      break;
    case ActionTypes.SET_NODE_SUMMARY:
      CommonStore.setStepSummary(action.data);
      CommonStore.emitChange();
      break;
    case ActionTypes.SET_NODE_CONNECTIONS:
      CommonStore.setStepConnections(action.connections);
      CommonStore.emitChange();
      break;
    case ActionTypes.UPDATE_NODE_CONNECTIONS:
      CommonStore.setStepConnections(action.connections, true);
      CommonStore.emit(EventTypes.UPDATE_GRAPH, {
        CONNECTION_UPDATE: action,
      });
      CommonStore.emitChange();
      break;
    case ActionTypes.UPDATE_STEP_POSITION:
      CommonStore.updateCurrentStepPosition(action.position);
      CommonStore.emitChange();
      break;
    case ActionTypes.ENABLE_SAVE:
      CommonStore.setSaveCallback(action.saveCallback);
      CommonStore.emitChange();
      break;
    case ActionTypes.DISABLE_SAVE:
      CommonStore.setSaveCallback(null);
      CommonStore.emitChange();
      break;
    case ActionTypes.RESET_EDITED:
      CommonStore.resetEdited();
      CommonStore.emitChange();
      break;
    case ActionTypes.UPDATE_CURRENT_LANGUAGE:
      CommonStore.setCurrentLanguage(
        action.languageSource,
        action.languageScript
      );
      CommonStore.emitChange();
      break;
    case ActionTypes.ADD_LANGUAGE_TO_BOT_SUCCESS:
      CommonStore.setSupportedLanguages(action.supportedLanguages);
      CommonStore.emitChange();
      break;

    case ActionTypes.SET_GLOBAL_CONSTANTS_DRAWER_ACTIVE:
      CommonStore.setGlobalConstantsDrawerFlag(action.flag);
      CommonStore.emitChange();
      break;

    case ActionTypes.SET_AB_TESTING_DRAWER_ACTIVE:
      CommonStore.setABTestingDrawerFlag(action.flag);
      CommonStore.emitChange();
      break;

    case ActionTypes.SET_AB_TESTING_RHS_STATE:
      CommonStore.setABTestingRhsState(action.rhsState);
      CommonStore.emitChange();
      break;

    case ActionTypes.SET_STEP_SELECTED_FOR_AB_TESTING:
      CommonStore.setStepSelectedForABTesting(action.step);
      CommonStore.emitChange();
      break;

    case ActionTypes.SET_AB_TEST_INFO:
      CommonStore.setABTestInfo(action.info);
      CommonStore.emitChange();
      break;

    case ActionTypes.SET_AB_TEST_MODIFIED_FLAG:
      CommonStore.setABTestModifiedFlag(action.flag);
      CommonStore.emitChange();
      break;

    case ActionTypes.SET_GEN_AI_MODAL_ACTIVE:
      CommonStore.setGenAiModalActive(action.flag);
      CommonStore.emitChange();
      break;

    default:
  }
});

export default CommonStore;
